//@ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import ManageBookingSkeleton from './ManageBookingSkeleton/ManageBookingSkeleton';
import BookingTotalsHeader from './BookingTotalsHeader/BookingTotalsHeader';
import BookingPricingPlansPanel from './BookingPricingPlansPanel/BookingPricingPlansPanel';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import { Box } from '@mui/material';
import { red } from '@mui/material/colors';
import { XGButton, XGModal } from '@xg-cl/xg-cl';
import { useState, useEffect, useCallback } from 'react';
import { CaretLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  getManageBookingById,
  deleteManageBookingPassengers
} from '../../../../services/booking/booking';
import * as PropTypes from 'prop-types';
import './ManageBooking.css';

/**
 * Functional React component for rendering a manage booking panel.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the manage booking panel component.
 * @param {string} props.bookingId - The ID of the booking to be managed.
 * @param {function} [props.handleManageBookingClose] - The function to be called when the close button is clicked.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the manage booking panel component.
 */
const ManageBooking = ({
  bookingId,
  handleManageBookingClose = () => {},
  ...rest
}) => {
  const { t } = useTranslation(['bookings']);
  const [bookingData, setBookingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPassengerRemoving, setIsPassengerRemoving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkedPassengers = bookingData?.passengers?.filter((passenger) => {
    return passenger?.checked;
  });

  const fetchBookingData = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await getManageBookingById(bookingId);

      setBookingData(data);
    } catch (error) {
      setBookingData(null);
    } finally {
      setIsLoading(false);
    }
  }, [bookingId]);

  const onCancelPassengerRemove = () => {
    const updatedPassengers = bookingData?.passengers?.map((passenger) => {
      return { ...passenger, checked: false };
    });

    setBookingData({ ...bookingData, passengers: updatedPassengers });
    setIsPassengerRemoving(false);
  };

  const onConfirmPassengerRemove = () => {
    setIsModalOpen(true);
  };

  const onSubmitRemovePassenger = async () => {
    try {
      setIsSubmitting(true);

      const idsToRemove = checkedPassengers?.map((passenger) => {
        return passenger?.passengerid;
      });

      const { data } = await deleteManageBookingPassengers(bookingId, {
        passengerids: idsToRemove
      });

      if (data && data?.deletedCount > 0) {
        const { data: newBookingData } = await getManageBookingById(bookingId);

        setBookingData(newBookingData);
        setIsPassengerRemoving(false);
        setIsModalOpen(false);
        SuccessToast(t('bookings:passengerRemovedSuccess'));
      } else {
        ErrorToast(t('bookings:passengerRemovedError'));
      }
    } catch (error) {
      ErrorToast(t('bookings:passengerRemovedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCloseRemovalModal = () => {
    setIsModalOpen(false);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId, fetchBookingData]);

  if (isLoading) {
    return <ManageBookingSkeleton id={'manage-booking-skeleton'} />;
  }

  return (
    <Box
      className='booking-panel-container'
      id={'manage-booking-panel'}
      {...rest}
    >
      <Box className='booking-panel__main-header'>
        <Box className='booking-breadcrumb__container'>
          <Box
            className='booking-breadcrumb__item'
            onClick={handleManageBookingClose}
          >
            <CaretLeft size={24} />
            <span>{t('bookings:bookings')}</span>
          </Box>
        </Box>
        <Box className='booking-panel__main-header__manage-booking'>
          <h2 className='booking-panel__main-header__manage-booking__title'>
            {t('bookings:manageBooking')}
          </h2>
          {!isPassengerRemoving && (
            <XGButton
              className='booking-panel__remove-passenger-button'
              data-testid='remove-passenger-button'
              id='remove-passenger-button'
              text={t('bookings:removePassenger')}
              type='error'
              size='large'
              variant='outlined'
              onClick={() => setIsPassengerRemoving((prev) => !prev)}
              disabled={!bookingData?.passengers?.length}
            />
          )}
        </Box>
      </Box>
      <BookingTotalsHeader
        id={'booking-totals-header'}
        data={bookingData?.totals}
      />
      <BookingPricingPlansPanel
        id={'booking-pricing-plans-panel'}
        data={bookingData}
        setBookingData={setBookingData}
        isPassengerRemoving={isPassengerRemoving}
      />

      {isPassengerRemoving && (
        <Box className='booking-panel__footer-cta__container'>
          <XGButton
            data-testid='cancel-remove-passenger-button'
            id='cancel-remove-passenger-button'
            text={t('common:cancel')}
            type='secondary'
            size='large'
            variant='outlined'
            onClick={onCancelPassengerRemove}
          />
          <XGButton
            data-testid='confirm-remove-passenger-button'
            id='confirm-remove-passenger-button'
            text={t('common:remove')}
            type='primary'
            size='large'
            variant='contained'
            onClick={onConfirmPassengerRemove}
            disabled={!checkedPassengers?.length}
          />
        </Box>
      )}

      <XGModal
        id={'remove-passenger-modal'}
        allowClosing={true}
        data-testid={'remove-passenger-modal'}
        actions={
          <Box
            className='booking-panel__modal-actions'
            data-testid='booking-panel__modal-actions'
          >
            <Box sx={{ position: 'relative' }}>
              <XGButton
                style={{
                  width: 'auto'
                }}
                className='booking-panel__remove-passenger-button w-auto '
                data-testid='update-action-cancel-button'
                id='update-action-cancel-button'
                text={t('bookings:confirmRemoval')}
                type='error'
                size='large'
                variant='outlined'
                onClick={onSubmitRemovePassenger}
                disabled={isSubmitting}
              />
              {isSubmitting && (
                <CircularProgress
                  id='update-actions-loader'
                  size={24}
                  sx={{
                    color: red[500],
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px'
                  }}
                />
              )}
            </Box>
            <XGButton
              data-testid='update-action-confirm-button'
              id='update-action-confirm-button'
              text={t('common:cancel')}
              type='secondary'
              size='large'
              variant='outlined'
              onClick={() => setIsModalOpen(false)}
              disabled={isSubmitting}
              style={{ width: 'auto' }}
            />
          </Box>
        }
        dismissable={false}
        open={isModalOpen}
        title={'Confirm Passenger Removal'}
        labelledby={'Confirm Passenger Removal'}
        maxWidth='lg'
        scroll='body'
        onClose={onCloseRemovalModal}
        {...rest}
      >
        <Box className='booking-panel__modal__content'>
          <Box className='booking-panel__modal__total-row'>
            <span className='booking-panel__modal__total-title'>
              Previous Booking Total
            </span>
            <span className='booking-panel__modal__total-title'>
              $0,000.00 USD
            </span>
          </Box>

          <span className='booking-panel__modal__divider' />

          <Box className='booking-panel__modal__totals-container w-100'>
            <Box className='w-100'>
              {/* 
              TODO:  keep this for near future implementation
              <Box className='booking-panel__modal__total-row'>
                <span className='booking-panel__modal__item-title'>
                  Double Room
                </span>
                <span className='booking-panel__modal__item-title'>
                  Total: $0,000.00 USD
                </span>
              </Box> */}

              <Box className='w-100 booking-panel__modal__subitems-container'>
                {checkedPassengers?.map((passenger, index) => {
                  return (
                    <Box
                      className='booking-panel__modal__total-row'
                      key={index}
                    >
                      <span className='booking-panel__modal__subitem-title'>
                        {index + 1}. {passenger?.fullname}
                      </span>
                      <span className='booking-panel__modal__subitem-title booking-panel__modal__subitem-total'>
                        - $0,000.00 USD
                      </span>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <span className='booking-panel__modal__divider' />

          <Box className='booking-panel__modal__total-row'>
            <span className='booking-panel__modal__total-title'>
              New Booking Total
            </span>
            <span className='booking-panel__modal__total-title'>
              $0,000.00 USD
            </span>
          </Box>
        </Box>
      </XGModal>
    </Box>
  );
};

ManageBooking.propTypes = {
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleManageBookingClose: PropTypes.func
};

export default ManageBooking;
